import { endOfDay, endOfWeek, startOfDay } from "date-fns";

import type { Priority, TaskStatus } from "../types";

/**
 * Board and task filters live in the URL search params, so a reload or a shared
 * link keeps them. Parsing ignores unknown values instead of failing.
 */

const PRIORITY_VALUES: Priority[] = ["low", "medium", "high", "urgent"];
const STATUS_VALUES: TaskStatus[] = ["todo", "doing", "blocked", "done"];
export const DUE_FILTERS = ["overdue", "today", "week", "none"] as const;
export type DueFilter = (typeof DUE_FILTERS)[number];

/** Value of the "card" task filter selecting the tasks with no card. */
export const FREE_TASKS = "free";

export interface CardFilters {
  q: string;
  salesId: string | null;
  tagIds: number[];
  source: string | null;
  priority: Priority | null;
}

export interface TaskFilters {
  q: string;
  salesId: string | null;
  status: TaskStatus | null;
  priority: Priority | null;
  contactId: string | null;
  due: DueFilter | null;
}

const oneOf = <T extends string>(values: readonly T[], value: string | null) =>
  value && (values as readonly string[]).includes(value) ? (value as T) : null;

export const normalizeText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export const parseCardFilters = (params: URLSearchParams): CardFilters => ({
  q: params.get("q") ?? "",
  salesId: params.get("user"),
  tagIds: (params.get("tags") ?? "")
    .split(",")
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0),
  source: params.get("source"),
  priority: oneOf(PRIORITY_VALUES, params.get("priority")),
});

export const parseTaskFilters = (params: URLSearchParams): TaskFilters => ({
  q: params.get("q") ?? "",
  salesId: params.get("user"),
  status: oneOf(STATUS_VALUES, params.get("status")),
  priority: oneOf(PRIORITY_VALUES, params.get("priority")),
  contactId: params.get("card"),
  due: oneOf(DUE_FILTERS, params.get("due")),
});

/** Writes a filter into the search params, removing it when empty. */
export const setSearchParam = (
  params: URLSearchParams,
  key: string,
  value: string | number[] | null | undefined,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  const serialized = Array.isArray(value) ? value.join(",") : (value ?? "");
  if (serialized) {
    next.set(key, serialized);
  } else {
    next.delete(key);
  }
  return next;
};

export const countActiveCardFilters = (filters: CardFilters): number =>
  [
    filters.q,
    filters.salesId,
    filters.tagIds.length > 0,
    filters.source,
    filters.priority,
  ].filter(Boolean).length;

export const countActiveTaskFilters = (filters: TaskFilters): number =>
  [
    filters.q,
    filters.salesId,
    filters.status,
    filters.priority,
    filters.contactId,
    filters.due,
  ].filter(Boolean).length;

/**
 * Translates the board filters into a data provider filter for
 * `pipeline_cards_summary`, so each column paginates the filtered cards (`q`
 * searches the name, e-mails and phones, see the Supabase provider callback).
 */
export const toCardServerFilter = (
  filters: CardFilters,
): Record<string, unknown> => ({
  ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
  ...(filters.salesId ? { sales_id: filters.salesId } : {}),
  ...(filters.tagIds.length > 0
    ? { "tags@cs": `{${filters.tagIds.join(",")}}` }
    : {}),
  ...(filters.source ? { source: filters.source } : {}),
  ...(filters.priority ? { priority: filters.priority } : {}),
});

const dueServerFilter = (
  due: DueFilter | null,
  now: Date,
): Record<string, string | null> => {
  switch (due) {
    case null:
      return {};
    case "none":
      return { "due_date@is": null };
    case "overdue":
      return { "due_date@lt": now.toISOString(), "done_date@is": null };
    case "today":
      return {
        "due_date@gte": startOfDay(now).toISOString(),
        "due_date@lte": endOfDay(now).toISOString(),
        "done_date@is": null,
      };
    case "week":
      return {
        "due_date@gte": startOfDay(now).toISOString(),
        "due_date@lte": endOfWeek(now).toISOString(),
        "done_date@is": null,
      };
  }
};

/**
 * Translates the task filters into a data provider filter, so the server
 * paginates the filtered tasks (`q` is a full-text search on title and
 * description, see the `tasks` lifecycle callback of the Supabase provider).
 */
export const toTaskServerFilter = (
  filters: TaskFilters,
  now: Date = new Date(),
): Record<string, unknown> => ({
  ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
  ...(filters.salesId ? { sales_id: filters.salesId } : {}),
  ...(filters.status ? { status: filters.status } : {}),
  ...(filters.priority ? { priority: filters.priority } : {}),
  ...(filters.contactId === FREE_TASKS
    ? { "contact_id@is": null }
    : filters.contactId
      ? { contact_id: filters.contactId }
      : {}),
  ...dueServerFilter(filters.due, now),
});
